// FILE: /src/Components/Apps/AppsDashboard.js

import React, { useEffect, useMemo, useState } from "react";
import { Input, Spin, Empty, message } from "antd";
import { useNavigate } from "react-router-dom";
import { HistoryOutlined, SearchOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { fetchAppsCatalog } from "../../services/apps";
import AppCard from "./AppCard";

const AppsDashboard = () => {
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const loadCatalog = async () => {
    setLoading(true);

    try {
      const response = await fetchAppsCatalog();
      setCatalog(response.apps || []);
      setCredits(response.credits || null);
    } catch (error) {
      message.error(error.message || "Failed to load Apps.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  const filteredApps = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const apps = catalog || [];

    if (!query) return apps;

    return apps.filter((app) => {
      return (
        app.name?.toLowerCase().includes(query) ||
        app.shortName?.toLowerCase().includes(query) ||
        app.description?.toLowerCase().includes(query) ||
        app.category?.toLowerCase().includes(query) ||
        app.appId?.toLowerCase().includes(query) ||
        app.slug?.toLowerCase().includes(query)
      );
    });
  }, [catalog, searchQuery]);

  const sortedApps = useMemo(() => {
    return [...filteredApps].sort((a, b) => {
      const statusPriority = {
        active: 1,
        coming_soon: 2,
        disabled: 3,
      };

      const aPriority = statusPriority[a.status] || 99;
      const bPriority = statusPriority[b.status] || 99;

      if (aPriority !== bPriority) return aPriority - bPriority;

      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [filteredApps]);

  const handleOpenApp = (app) => {
    if (app.appId === "background.remove" || app.slug === "background-remover") {
      navigate("/Dashboard/Apps/background-remover");
      return;
    }

    if (app.appId === "caption.generate" || app.slug === "caption-generator") {
      navigate("/Dashboard/Apps/caption-generator");
      return;
    }

    message.info(`${app.name} is not available yet.`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-700 via-indigo-700 to-slate-900 rounded-3xl px-6 py-14 sm:px-10 sm:py-9 text-white shadow-xl">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4">
            Creative Apps
          </h1>

          <p className="text-purple-100 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            Generate, transform, and enhance your brand assets using AI-powered tools.
            Built to help you create faster with your existing credits.
          </p>

          <div className="mt-8 max-w-2xl mx-auto">
            <Input
              size="large"
              allowClear
              prefix={<SearchOutlined className="text-gray-400" />}
              placeholder="Search apps, tools, or workflows..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="rounded-xl"
            />
          </div>

          <button
            type="button"
            onClick={() => navigate("/Dashboard/Apps/history")}
            className="mt-6 inline-flex items-center justify-center gap-2 text-sm font-semibold text-purple-100 hover:text-white transition-colors bg-transparent border-none"
          >
            <HistoryOutlined />
            View Recent Activity
            <ArrowRightOutlined className="text-xs" />
          </button>
        </div>

        <div className="absolute -right-16 -bottom-16 w-64 h-64 bg-white/10 rounded-full blur-2xl"></div>
        <div className="absolute right-20 top-8 w-32 h-32 bg-purple-300/20 rounded-full blur-xl"></div>
        <div className="absolute -left-20 top-10 w-56 h-56 bg-indigo-300/10 rounded-full blur-2xl"></div>
      </section>

      <section className="mt-8 bg-white border border-gray-200 rounded-3xl shadow-sm p-5 sm:p-7 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
          <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-1">
              Available Apps
            </h2>

            <p className="text-sm sm:text-base text-gray-500 mb-0">
              Powerful tools to enhance your creative output.
            </p>
          </div>

          {!loading && (
            <div className="text-sm text-gray-500">
              {sortedApps.length} app{sortedApps.length === 1 ? "" : "s"} found
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-80">
            <Spin size="large" />
          </div>
        ) : sortedApps.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl border border-dashed p-12">
            <Empty description="No apps found." />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {sortedApps.map((app) => (
              <AppCard
                key={app.appId}
                app={app}
                credits={credits}
                onOpen={handleOpenApp}
              />
            ))}
          </div>
        )}
      </section>

      
    </div>
  );
};

export default AppsDashboard;